import { useEffect, useState } from 'react'
import { useQueryClient } from 'react-query'

import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { filesKeys } from 'hooks/queries/files/filesKeys'
import { scheduleKeys } from 'hooks/queries/schedules/scheduleKeys'
import { type AlmanacFileIndexItem } from 'hooks/queries/files/types'
import { type BrandPlanSchedule } from 'types'

export type CalendarLoadingState = {
  hasLoading: boolean
  scheduleId: number | null
  loadingCount: number
}

const EMPTY: CalendarLoadingState = { hasLoading: false, scheduleId: null, loadingCount: 0 }

export const useCalendarLoadingState = (enabled: boolean = true): CalendarLoadingState => {
  const queryClient = useQueryClient()
  const workspaceId = useCurrentWorkspaceId()

  const compute = (): CalendarLoadingState => {
    if (!enabled) return EMPTY
    let loadingCount = 0
    let firstLoadingScheduleId: number | null = null

    const calendarCaches = queryClient.getQueriesData<AlmanacFileIndexItem[]>(filesKeys.partialCalendar(workspaceId))
    for (const [, files] of calendarCaches) {
      if (!Array.isArray(files)) continue
      for (const f of files) {
        if (f.is_loading) {
          loadingCount += 1
          if (firstLoadingScheduleId === null) {
            firstLoadingScheduleId = f.campaign_info?.id ?? null
          }
        }
      }
    }

    const scheduleCaches = queryClient.getQueriesData<BrandPlanSchedule>(scheduleKeys.details())
    for (const [, schedule] of scheduleCaches) {
      const contents = schedule?.contents
      if (!Array.isArray(contents)) continue
      for (const c of contents) {
        if (c.almanac_file?.is_loading) {
          loadingCount += 1
          if (firstLoadingScheduleId === null) {
            firstLoadingScheduleId = schedule?.id ?? null
          }
        }
      }
    }

    return loadingCount > 0 ? { hasLoading: true, scheduleId: firstLoadingScheduleId, loadingCount } : EMPTY
  }

  const [state, setState] = useState<CalendarLoadingState>(compute)

  useEffect(() => {
    if (!enabled) {
      setState((prev) => (prev === EMPTY ? prev : EMPTY))
      return
    }
    setState((prev) => {
      const next = compute()
      return sameState(prev, next) ? prev : next
    })
    const unsubscribe = queryClient.getQueryCache().subscribe(() => {
      setState((prev) => {
        const next = compute()
        return sameState(prev, next) ? prev : next
      })
    })
    return () => {
      unsubscribe()
    }
    // queryClient is stable; workspaceId can change on workspace switch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, queryClient, workspaceId])

  return state
}

const sameState = (a: CalendarLoadingState, b: CalendarLoadingState): boolean =>
  a.hasLoading === b.hasLoading && a.scheduleId === b.scheduleId && a.loadingCount === b.loadingCount
